import React, { useCallback, useMemo, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Paper,
    Grid,
    Stack,
    Divider,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import ImageUploadInput from "components/molecules/ImageUploadInput";

const GridSelector = ({
    title = "Grid Items",
    noItemsText = "No grid items added yet. Click the + button to add your first item.",
    value,
    attribute,
    handleAttributeChange,
}) => {
    const gridItems = value;
    const attributes = attribute.attributes;

    const handleAddItem = useCallback(() => {
        const newItem = {};
        attributes.forEach((attr) => {
            newItem[attr.code] = attr.type === "string" ? "" : null;
        });
        handleAttributeChange(attribute.key, [...gridItems, newItem]);
    }, [gridItems, attributes, attribute.key, handleAttributeChange]);

    const handleRemoveItem = useCallback(
        (index) => {
            const newItems = gridItems.filter((_, i) => i !== index);
            handleAttributeChange(attribute.key, newItems);
        },
        [gridItems, attribute.key, handleAttributeChange],
    );

    const handleItemChange = (index, field, fieldValue) => {
        const newItems = [...gridItems];
        newItems[index] = {
            ...newItems[index],
            [field]: fieldValue,
        };
        handleAttributeChange(attribute.key, newItems);
    };

    const renderField = (attr, itemValue, itemIndex) => {
        const fieldValue = itemValue[attr.code] || "";
        switch (attr.input_type) {
            case "image":
                return (
                    <ImageUploadInput
                        value={fieldValue}
                        logoUrl={itemValue[`${attr.code}`] || ""}
                        onChange={(file) =>
                            handleItemChange(itemIndex, attr.code, file)
                        }
                        onLogoUrlChange={(url) =>
                            handleItemChange(itemIndex, `${attr.code}`, url)
                        }
                        label={`Upload ${attr.title}`}
                        acceptedFileTypes={[
                            "image/png",
                            "image/jpeg",
                            "image/jpg",
                        ]}
                    />
                );
            case "input":
                if (attr.code === "description") {
                    return (
                        <Box sx={{ position: "relative" }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    mb: 1,
                                    fontWeight: 500,
                                    color: "text.primary",
                                }}
                            >
                                {attr.title}
                                {attr.required && (
                                    <span style={{ color: "red" }}> *</span>
                                )}
                            </Typography>
                            <textarea
                                value={fieldValue}
                                onChange={(e) =>
                                    handleItemChange(
                                        itemIndex,
                                        attr.code,
                                        e.target.value,
                                    )
                                }
                                placeholder={attr.placeholder}
                                style={{
                                    width: "100%",
                                    minHeight: "80px",
                                    maxHeight: "200px",
                                    padding: "12px 14px",
                                    border: "1px solid #c4c4c4",
                                    borderRadius: "4px",
                                    fontSize: "16px",
                                    fontFamily: "inherit",
                                    lineHeight: "1.5",
                                    resize: "vertical",
                                    outline: "none",
                                    transition: "border-color 0.2s ease",
                                    backgroundColor: "transparent",
                                    boxSizing: "border-box",
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = "#1976d2";
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = "#c4c4c4";
                                }}
                            />
                        </Box>
                    );
                }
                return (
                    <TextField
                        fullWidth
                        label={attr.title}
                        value={fieldValue}
                        onChange={(e) =>
                            handleItemChange(
                                itemIndex,
                                attr.code,
                                e.target.value,
                            )
                        }
                        required={attr.required}
                        placeholder={attr.placeholder}
                        variant="outlined"
                    />
                );
            default:
                return (
                    <TextField
                        fullWidth
                        label={attr.title}
                        value={fieldValue}
                        onChange={(e) =>
                            handleItemChange(
                                itemIndex,
                                attr.code,
                                e.target.value,
                            )
                        }
                        required={attr.required}
                        placeholder={attr.placeholder}
                        variant="outlined"
                    />
                );
        }
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {title} ({gridItems.length})
                </Typography>
                <IconButton onClick={handleAddItem}>
                    <AddIcon />
                </IconButton>
            </Box>

            {gridItems.length === 0 ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 4,
                        textAlign: "center",
                        bgcolor: "grey.50",
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {noItemsText}
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={3}>
                    {gridItems.map((item, index) => (
                        <Paper
                            key={index}
                            variant="outlined"
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                bgcolor: "background.paper",
                                position: "relative",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2,
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 500 }}
                                >
                                    Item {index + 1}
                                </Typography>
                                <IconButton
                                    onClick={() => handleRemoveItem(index)}
                                    size="small"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            {attributes.map((attr) => (
                                <Box sx={{ mb: 2 }}>
                                    {renderField(attr, item, index)}
                                </Box>
                            ))}
                        </Paper>
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export default GridSelector;
